import { volumeBundleByProductId } from '../../api/bundle';
import { trapFocusInBundle } from '../../dynamicBundle/index.js';
import { setUniformProductImageHeight } from '../../product-item/index.js';
import {
  MODAL_WRAPPER_SELECTOR,
  PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL,
  VOLUME_BUNDLE_ACTION,
  VOLUME_BUNDLE_BLOCK_SELECTOR,
  VOLUME_BUNDLE_ID_CONTAINER_PRODUCT,
  VOLUME_BUNDLE_NAME_TEMPLATE,
} from '../constants.js';
import { registerEventBundle, registerEventBundleModal } from '../events/bundle-events';
import { getVolumeBundleSettingsByWidgetId, volumeBundleData } from '../services/bundle-data';
import { getVolumeBundleWidgetTemplate } from '../services/template-service';

/**
 * @type {import('../index.d').renderVolumeBundleWithData}
 */
export const renderVolumeBundleWithData = async (
  context,
  widgetId,
  volumeBundleData,
  wrapper,
  name,
  callBack
) => {
  const templateVolumeBundle = await getVolumeBundleWidgetTemplate(context, name);

  const html = await context.templateRender(templateVolumeBundle, {
    bundle: volumeBundleData,
    ...getVolumeBundleSettingsByWidgetId(context, widgetId),
    locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
  });

  if (wrapper) {
    wrapper.innerHTML = html;
    const bundleModal = document
      ?.getElementById(`${PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL}${context.blockType}`)
      ?.querySelector(MODAL_WRAPPER_SELECTOR);
    if (bundleModal) {
      trapFocusInBundle(bundleModal, context, true);
    }
  } else {
    const bundleModal = document
      ?.getElementById(`${PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL}${context.blockType}`)
      ?.querySelector(MODAL_WRAPPER_SELECTOR);

    if (bundleModal) {
      bundleModal.innerHTML = html;
      bundleModal.style.display = 'block';
      trapFocusInBundle(bundleModal, context, true);
    }
  }

  if (typeof callBack === 'function') {
    callBack();
  }
};

/**
 * @type {import('../index.d').renderVolumeBlocksForProductPage}
 */
export const renderVolumeBlocksForProductPage = async (context, productId) => {
  context.widgetId = 'bc-volume-bundle';
  const domPredictiveBundles = document?.querySelectorAll(VOLUME_BUNDLE_BLOCK_SELECTOR);
  const position = 'volumeBlock';

  if (!domPredictiveBundles.length) return;

  const getVolumeBundles = await volumeBundleByProductId(context, productId);
  if (!getVolumeBundles.length) return;

  const volumeBundle = getVolumeBundles.find((bundle) => bundle.bundle_type === 'volume');
  if (!volumeBundle) return;

  volumeBundle.currentProductId = productId;
  const dataVolumeBundle = await volumeBundleData(context, volumeBundle, {
    optionData: VOLUME_BUNDLE_ACTION.initData,
    target: domPredictiveBundles,
    position,
  });
  const templateVolumeBundle = await getVolumeBundleWidgetTemplate(context);

  const domBundleInnerHtml = await context.templateRender(templateVolumeBundle, {
    bundle: dataVolumeBundle,
    ...getVolumeBundleSettingsByWidgetId(context, dataVolumeBundle.widgetId),
    locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
  });

  domPredictiveBundles.forEach((domBundle) => {
    if (dataVolumeBundle.bundle_type === 'volume') {
      domBundle.innerHTML = domBundleInnerHtml;
      registerEventBundle(context, domBundle, position);
      trapFocusInBundle(domBundle, context, false);
    }
  });
};

/**
 * @type {import('../index.d').renderVolumeBundleForSearchPage}
 */
export const renderVolumeBundleForSearchPage = async (context) => {
  const domVolumeBundleProductList = document?.getElementById(VOLUME_BUNDLE_ID_CONTAINER_PRODUCT);
  const position = 'productList';

  const bundleTypeVolume = context?.dynamicBundles?.find(
    (bundle) => bundle?.bundle_type === 'volume'
  );

  // Early return if requirements not met
  if (!domVolumeBundleProductList) {
    return;
  }

  if (!bundleTypeVolume) {
    domVolumeBundleProductList.setAttribute('data-bundle-status', 'no-bundle-found');
    return;
  }

  domVolumeBundleProductList.setAttribute('data-bundle-status', 'loading');

  const dataVolumeBundle = await volumeBundleData(context, bundleTypeVolume, {
    optionData: VOLUME_BUNDLE_ACTION.initData,
    target: domVolumeBundleProductList,
    position,
  });

  // If volumeBundleData returns undefined (e.g., no valid variants), exit early
  if (!dataVolumeBundle) {
    domVolumeBundleProductList.setAttribute('data-bundle-status', 'no-valid-variants');
    domVolumeBundleProductList.innerHTML = '';
    return;
  }

  domVolumeBundleProductList.setAttribute('data-bundle-status', 'rendering');

  const templateVolumeBundle = await getVolumeBundleWidgetTemplate(
    context,
    VOLUME_BUNDLE_NAME_TEMPLATE.bundleProduct
  );

  domVolumeBundleProductList.innerHTML = await context.templateRender(templateVolumeBundle, {
    bundle: dataVolumeBundle,
    ...getVolumeBundleSettingsByWidgetId(context, dataVolumeBundle?.widgetId),
  });

  domVolumeBundleProductList.setAttribute('data-bundle-status', 'rendered');

  registerEventBundleModal(context, domVolumeBundleProductList, position);
  setUniformProductImageHeight(
    context,
    context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnMobile || 2,
    context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnDesktop || 3,
    context.app.generalSettings?.breakpointmobile || 575,
    context.app.generalSettings?.breakpointtabletportraitmax || 991,
    3
  );
};

/**
 * @type {import('../index.d').updateBundleUI}
 */
export const updateBundleUI = (wrapper, bundleData, target) => {
  if (!bundleData || !wrapper) return;

  const modalContent = wrapper.closest('.boost-sd__volume-bundle-modal-content');
  const productImage = modalContent
    ? modalContent.querySelector(
        '.boost-sd__volume-bundle-product-image .boost-sd__product-image-img'
      )
    : null;

  const productOriginalPrice = wrapper.querySelector(
    '.boost-sd__volume-bundle-menu-original-price'
  );
  const productDiscountedPrice = wrapper.querySelector(
    '.boost-sd__volume-bundle-menu-discounted-price'
  );

  if (productImage) productImage.src = bundleData.matchedProduct.selectedOption.image;
  if (productOriginalPrice)
    productOriginalPrice.textContent = bundleData.selectedVariant.originalPrice;
  if (productDiscountedPrice)
    productDiscountedPrice.textContent = bundleData.selectedVariant.discountedPrice;
};
